import { useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  Banner,
  BannerList,
  Button,
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";

/**
 * Shown when a Best Interest Decision is started without a capacity assessment
 * behind it. A best interests decision only arises once someone is found to lack
 * capacity, so the clinician is offered the assessment first — but never forced,
 * since either form may legitimately be completed on its own.
 */
export function ConsiderFdmcaDialog({
  onContinueToBid,
  onStartFdmca,
}: {
  onContinueToBid: () => Promise<void>;
  onStartFdmca: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);

  const run = async (action: () => Promise<void>) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await action();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PopupDialog open={true} fullWidth testId="consider-fdmca-dialog">
      {/* The Best Interest Decision has already been created by the time this
          is shown, so dismissing is the same as declining the assessment and
          carrying on with it. */}
      <PopupDialogTitle
        titleText={t("pages.mcaAssessment.considerFdmcaDialog.heading")}
        closeDialog={() => run(onContinueToBid)}
      />
      <Banner
        title={t("pages.mcaAssessment.considerFdmcaDialog.body")}
        bannerType={BannerList.INFO}
      />
      <Box
        sx={{
          mt: 2,
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          variant="outlined"
          testId="continue-to-bid-button"
          disabled={submitting}
          label={t("pages.mcaAssessment.considerFdmcaDialog.continueToBid")}
          onClick={() => run(onContinueToBid)}
        />
        <Button
          testId="start-fdmca-button"
          disabled={submitting}
          label={t("pages.mcaAssessment.considerFdmcaDialog.startFdmca")}
          onClick={() => run(onStartFdmca)}
        />
      </Box>
    </PopupDialog>
  );
}
